import React, { useRef, useImperativeHandle, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { Icons, Box, TextField, DateIntervalField, SelectField, MenuItem, Button, InputAdornment, Popover, FormControlLabel, Checkbox, Stack } from "@curves/ui-base";
import { parseQueryStringToArray } from "@util/utils";
import { parseDate } from "@util/moment";

const ProspectOverviewSearchBar = React.forwardRef((props, ref) => {
	const { sourceData: q, options = {} } = props;
	const paramsData = q || {};
	const { validOptions = [], requiredOptions, sourceOptions } = options || {};
	const validTypeRef = useRef(null);
	const sourceTypesRef = useRef(null);
	const requiredTypesRef = useRef(null);
	const nameRef = useRef(null);
	const dateRangeRef = useRef(null);

	const getParams = () => {
		let result = {
			isAdvanced: true,
		};

		if (nameRef.current && nameRef.current.getResult) {
			Object.assign(result, { name: nameRef.current.getResult() });
		}

		if (validTypeRef.current && validTypeRef.current.getResult) {
			Object.assign(result, { validType: validTypeRef.current.getResult() });
		}

		if (sourceTypesRef.current && sourceTypesRef.current.getResult) {
			Object.assign(result, { source: sourceTypesRef.current.getResult() });
		}

		if (requiredTypesRef.current && requiredTypesRef.current.getResult) {
			Object.assign(result, { required: requiredTypesRef.current.getResult() });
		}

		if (dateRangeRef.current && dateRangeRef.current.getResult) {
			Object.assign(result, dateRangeRef.current.getResult());
		}

		return result;
	};

	useImperativeHandle(
		ref,
		() => {
			return {
				getResult: () => getParams(),
			};
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<Grid container spacing={2}>
			<Grid xs={4} item>
				<TextField
					key={paramsData.name}
					ref={nameRef}
					label="名單"
					defaultValue={paramsData.name}
					InputProps={{
						placeholder: "請輸入姓名/手機號碼(擇一即可)",
					}}
					fullWidth
				/>
			</Grid>
			<Grid xs={8} item>
				<DateIntervalField
					key={`${paramsData.startDate}_${paramsData.endDate}`}
					ref={dateRangeRef}
					label="詢問日期"
					startDateProps={{
						defaultValue: parseDate(paramsData.startDate),
					}}
					endDateProps={{
						defaultValue: parseDate(paramsData.endDate),
					}}
					fullWidth
				/>
			</Grid>
			<Grid xs={4} item>
				<SelectField key={paramsData.validType} ref={validTypeRef} defaultValue={paramsData.validType || 2} fullWidth>
					{validOptions.map(({ text, value, disabled }) => (
						<MenuItem value={value} key={text} disabled={disabled}>
							{text}
						</MenuItem>
					))}
				</SelectField>
			</Grid>
			<Grid xs={4} item>
				<MultipleSelectCheckedBox key={paramsData.source} label="來源" options={sourceOptions} ref={sourceTypesRef} defaultValue={paramsData.source} />
			</Grid>
			<Grid xs={4} item>
				<MultipleSelectCheckedBox key={paramsData.required} label="需求" options={requiredOptions} ref={requiredTypesRef} defaultValue={paramsData.required} />
			</Grid>
			<Grid xs={12} item>
				<Stack justifyContent="end">
					<Button type="submit" variant="contained">
						搜尋
					</Button>
				</Stack>
			</Grid>
		</Grid>
	);
});

// 變形下拉式選單(內容有checkbox)
const MultipleSelectCheckedBox = React.forwardRef((props, ref) => {
	const { options = [], label, showLabel = false, defaultValue } = props;
	const [anchorEl, setAnchorEl] = useState(null);
	const [checkedState, setChecked] = useState({});
	const preloadRef = useRef(false);

	const handleDefaultValue = defaultValue => {
		let result = {};

		if (typeof defaultValue === "string") {
			defaultValue = parseQueryStringToArray(defaultValue);
		}

		if (Array.isArray(defaultValue) && defaultValue.length > 0) {
			defaultValue.forEach(item => {
				result[item] = true;
			});
		}
		setChecked(result);
	};

	const handleAnchorElClick = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleAnchorElClose = () => {
		setAnchorEl(null);
	};

	const onCheckedChange = (e, isChecked) => {
		setChecked(pre => ({
			...pre,
			[e.target.value]: isChecked,
		}));
	};

	useImperativeHandle(
		ref,
		() => {
			return {
				getResult: () => {
					let result = Object.entries(checkedState)
						.filter(([key, value]) => value)
						.map(([num]) => num);
					return result;
				},
			};
		},
		// eslint-disable-next-line
		[checkedState]
	);

	useEffect(
		() => {
			if (!preloadRef.current) {
				handleDefaultValue(defaultValue);
				preloadRef.current = true;
			}
		},
		// eslint-disable-next-line
		[defaultValue]
	);

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;
	const counts = Object.values(checkedState).filter(isTrue => isTrue).length;

	return (
		<React.Fragment>
			<TextField
				label={showLabel && label}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end" className="cursor-pointer">
							<Icons.ArrowDropDown />
						</InputAdornment>
					),
				}}
				value={label ? `${label}(${counts})` : ""}
				inputStyle={{ cursor: "pointer" }}
				inputRootStyle={{ cursor: "pointer" }}
				onClick={handleAnchorElClick}
				fullWidth
				readOnly
			/>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleAnchorElClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
			>
				<Box className="py-2 px-4" style={{ width: 520 }}>
					<Grid container spacing={2}>
						{options.map(({ text, value, disabled }) => {
							return (
								<Grid item xs={4} key={text}>
									<FormControlLabel
										control={<Checkbox key={!!checkedState[value]} value={value} onChange={onCheckedChange} defaultChecked={!!checkedState[value]} />}
										label={text}
										disabled={disabled}
									/>
								</Grid>
							);
						})}
					</Grid>
				</Box>
			</Popover>
		</React.Fragment>
	);
});

export default ProspectOverviewSearchBar;
